import { z } from 'zod';
// B3: the shadow column (`EntityMeta.shadow`) — pure vocabulary, no three. See `registry.ts`.
import { ENTITY_SHADOW } from '../../render/lights/lightModel';
import type { RegistryEntry } from '../registry';
// F1: the ONE placement-rotation fragment (yaw + pitch + roll), shared with the main table.
import { PLACEMENT_ROTATION } from '../../world/schema';

/**
 * Exploration vertical SUB-TABLE (X0 seam 2; ADR-0001 amendment): an eager-safe table fragment
 * `src/entities/registry.ts` spreads into ENTITY_REGISTRY with one line — so the vertical adds
 * classes here without ever touching the shared registry file again. Same contract as the main
 * table: names + zod schemas + meta + lazy `() => import(...)` thunks ONLY. NEVER a static import
 * of an entity class module from this file — the ADR-0001 boundary rule applies to sub-tables
 * identically (tests/entityBoundary.test.ts scans this file like registry.ts).
 *
 * X1c: the real exploration classes (plan §1.2 registry-class table). The X0 exploration_marker
 * seam-proof dummy is retired — these replace it. `axon_companion` landed with Axon Phase 1;
 * npc_holder / charge_pickup / mission_start_board land in later phases.
 */

const V3Tuple = z.tuple([z.number(), z.number(), z.number()]);

/**
 * Director nomination policy (D43 values, plan §1.2) — data, not code: nominate.ts consumes it.
 * `maxRoaming` is schema-present day one; v1 ships zero roamers.
 */
export const NominationPolicySchema = z
  .object({
    shortlistSize: z.number().int().min(1).default(3),
    minHopM: z.number().min(0).default(15),
    beatHopM: z.number().min(0).default(40),
    // Capped at RECENT_HOLDERS_CAP (src/exploration/core/sessionState.ts): the reducer trims
    // state.recentHolders to that length, so a policy asking for more would silently get less.
    // The literal is duplicated because this table is EAGER and may not import the vertical's
    // core (ADR-0001) — tests/exploration/reducerEdges.test.ts pins the two together.
    recentHolderMemory: z.number().int().min(0).max(8).default(6),
    preferNamedAtKbOpenings: z.boolean().default(true),
    maxRoaming: z.number().int().min(0).default(0),
  })
  .default({
    shortlistSize: 3,
    minHopM: 15,
    beatHopM: 40,
    recentHolderMemory: 6,
    preferNamedAtKbOpenings: true,
    maxRoaming: 0,
  });

export const ExplorationDirectorSchema = z.object({
  type: z.literal('exploration_director'),
  /** Marker position, world meters (editor-ghost only — the director has no gameplay body). */
  pos: V3Tuple,
  /** Nomination policy — its PRESENCE in a world is what makes the world open-world (no flag). */
  policy: NominationPolicySchema,
  /** Casting roster id (content/exploration/rosters.json — wired in a later phase). */
  rosterId: z.string().optional(),
});

export type ExplorationDirectorDef = z.infer<typeof ExplorationDirectorSchema>;

export const ExplorationAnchorSchema = z.object({
  type: z.literal('exploration_anchor'),
  /** Holder spawn position, world meters. */
  pos: V3Tuple,
  /** Facing, degrees about +Y (0 = game forward, −Z). */
  ...PLACEMENT_ROTATION,
  /**
   * Weighted holder-class list — ONE class is drawn at start via castRng (explicit `weight`
   * replaces UE's duplicate-entry hack: same behavior, honest data).
   */
  holders: z
    .array(z.object({ class: z.string(), weight: z.number().positive().default(1) }))
    .default([{ class: 'artifact_station', weight: 1 }]),
  /** Pin a specific profile to this anchor (beats any roster draw; casting.ts ladder rung 2). */
  pinnedProfile: z.string().optional(),
  /** Roaming fields exist day one (D1); v1 ships zero roamers. */
  canWander: z.boolean().default(false),
  eligibleForRouting: z.boolean().default(true),
});

export type ExplorationAnchorDef = z.infer<typeof ExplorationAnchorSchema>;

export const ArtifactStationSchema = z.object({
  type: z.literal('artifact_station'),
  /** Base position, world meters. */
  pos: V3Tuple,
  ...PLACEMENT_ROTATION,
  /** Display kind ("a crate is as legitimate a holder as a professor") — kindId is its slug. */
  kindName: z.string().min(1).default('supply crate'),
  /** WORLD MESH ASSET id for a skinned look (later); absent ⇒ placeholder crate visual. */
  meshAsset: z.string().optional(),
  /** Open-flourish style hint for presentation (data through; capped 1.2 s by the step machine). */
  openFlourish: z.string().default('chest'),
});

export type ArtifactStationDef = z.infer<typeof ArtifactStationSchema>;

export const StationGateSchema = z.object({
  type: z.literal('station_gate'),
  /** Gate center (on the route surface), world meters. */
  pos: V3Tuple,
  ...PLACEMENT_ROTATION,
  /** Overlap radius, meters. */
  radius: z.number().positive().default(2.5),
  /** Position along the authored route — RoutePlacement plays gates in this order. */
  routeOrder: z.number().int().min(0).default(0),
  /**
   * Jetpack-style 'gate' (blocks) vs Mountain-style 'celebrate' (marks) — per-gate DATA.
   * Default 'celebrate' per plan §4.2; OWNER RULING PENDING on the default — flip here if ruled.
   */
  gateMode: z.enum(['celebrate', 'gate']).default('celebrate'),
  /** Optional group unlocked when this gate clears (later phases). */
  unlocksGroup: z.string().optional(),
});

export type StationGateDef = z.infer<typeof StationGateSchema>;

export const CheckpointSchema = z.object({
  type: z.literal('checkpoint'),
  /** Respawn feet position, world meters. */
  pos: V3Tuple,
  /** Respawn facing, degrees about +Y. */
  ...PLACEMENT_ROTATION,
  /** Activation radius, meters. */
  radius: z.number().positive().default(3),
  /** The route's starting checkpoint — the respawn target before any activation. */
  initial: z.boolean().default(false),
});

export type CheckpointDef = z.infer<typeof CheckpointSchema>;

export const KillVolumeSchema = z.object({
  type: z.literal('kill_volume'),
  /** Box CENTER, world meters. */
  pos: V3Tuple,
  /** Full box dimensions, meters ([x, y, z]). */
  size: V3Tuple.default([4, 2, 4]),
});

export type KillVolumeDef = z.infer<typeof KillVolumeSchema>;

/**
 * The Axon companion (plan §8 Phase 1). Placement data ONLY — everything about how he behaves
 * lives in `content/tuning/axon.json`, and everything about what he decides lives in the pure
 * brain. There is deliberately no appearance field: Phase 1 draws a capsule and a cone, and a
 * knob for a look that does not exist yet is a knob that will be wrong when it does.
 *
 * A world gets a companion by placing ONE of these and listing `axon_companion` in `requires` —
 * which, because the gate is a predicate over this sub-table's keys, is also what arms the
 * exploration chunk load. A world with no placement renders exactly as it did before.
 */
export const AxonCompanionSchema = z.object({
  type: z.literal('axon_companion'),
  /** Where he materialises at world start, world metres (a FEET position, like every other class). */
  pos: V3Tuple,
  /** Height above `pos` he spawns at — his own hover height, so he never starts inside the floor. */
  hoverOffsetM: z.number().min(0).default(0.95),
});

export type AxonCompanionDef = z.infer<typeof AxonCompanionSchema>;

export const EXPLORATION_ENTITIES: Record<string, RegistryEntry> = {
  exploration_director: {
    schema: ExplorationDirectorSchema,
    meta: {
      category: 'logic',
      label: 'Exploration director',
      description:
        'Its presence makes the world an open-world exploration level; carries the nomination policy. Editor-only ghost marker.',
      shadow: ENTITY_SHADOW.ghost,
      mobility: 'dynamic',
    },
    load: () => import('./director'),
  },
  exploration_anchor: {
    schema: ExplorationAnchorSchema,
    meta: {
      category: 'logic',
      label: 'Exploration anchor',
      description:
        'Holder spawn point: draws ONE holder class from a weighted list at world start. Editor-only ghost marker.',
      // The ghost is hidden in play; the crate it draws at world start is parented to `ctx.root` from
      // a callback and takes `ENTITY_SHADOW_DEFAULT` (the host cannot attribute it) — same cast/receive.
      shadow: ENTITY_SHADOW.prop,
      mobility: 'dynamic',
    },
    load: () => import('./anchor'),
  },
  artifact_station: {
    schema: ArtifactStationSchema,
    meta: {
      category: 'interactable',
      label: 'Artifact station',
      description: 'Static artifact holder (crate/kiosk); press-E hand-over, open flourish.',
      shadow: ENTITY_SHADOW.prop,
      mobility: 'dynamic',
    },
    load: () => import('./artifactStation'),
  },
  station_gate: {
    schema: StationGateSchema,
    meta: {
      category: 'interactable',
      label: 'Station gate',
      description:
        'Route-world artifact gate: walk-through overlap trigger, played in routeOrder.',
      shadow: ENTITY_SHADOW.prop,
      mobility: 'dynamic',
    },
    load: () => import('./stationGate'),
  },
  checkpoint: {
    schema: CheckpointSchema,
    meta: {
      category: 'logic',
      label: 'Checkpoint',
      description:
        'Respawn target: activates on player proximity. Editor-only ghost marker.',
      shadow: ENTITY_SHADOW.ghost,
      mobility: 'dynamic',
    },
    load: () => import('./checkpoint'),
  },
  kill_volume: {
    schema: KillVolumeSchema,
    meta: {
      category: 'logic',
      label: 'Kill volume',
      description:
        'Overlap box that respawns the player at the last checkpoint. Editor-only ghost box.',
      shadow: ENTITY_SHADOW.ghost,
      mobility: 'dynamic',
    },
    load: () => import('./killVolume'),
  },
  axon_companion: {
    schema: AxonCompanionSchema,
    meta: {
      category: 'logic',
      label: 'Axon companion',
      description:
        'The companion who follows the player, holds a standoff and stays out of the sightline. One per world; a second is refused.',
      // `cast: false` is MEASURED (three r185 pulls a custom-`colorNode` material into the shadow
      // pass and throws — `docs/ai/tsl-patterns.ts` pattern 3); `receive: true` is the owner's fix.
      shadow: ENTITY_SHADOW.companion,
      mobility: 'dynamic',
    },
    load: () => import('./axonCompanion'),
  },
};
